import { createApi } from '@reduxjs/toolkit/query/react'

import { getQueryReservaPorIdOData, getQueryReservasOData } from '../constants/queryReservasOData'
import { ReservaKanban } from '../interfaces/ReservaCentralizada'
import { ReservaCentralizadaBase } from '../interfaces/ReservaCentralizadaDetalleModal'

import { baseQuery } from '@/core/api/baseQuery'
import { GenericResponseOData } from '@/core/api/interfaces/GenericResponseOData'
import { reportarAdvertencia } from '@/core/telemetria/reportarError'

export interface GetBookingParams {
  id: number | undefined
}

export interface GetBookingWithFullSearchParams {
  textoBusqueda: string
  fechaInicio: string
  fechaFin: string
  esFechaOperacion: boolean
  menuId: number
}

// El tablero pide todas las reservas del menú sin paginar. Hoy el menú más grande tiene ~1300
// filas contra un PageSize de 10000, así que no hace falta seguir el @odata.nextLink todavía.
// ponytail: esto es la alarma, no la solución — si el total del backend supera lo que llegó,
// faltan cards y hay que implementar el seguimiento del nextLink.
const avisarSiFaltanFilas = (
  response: GenericResponseOData<ReservaKanban[]>,
): GenericResponseOData<ReservaKanban[]> => {
  const total = Number(response['@odata.count'])
  const recibidas = response.value?.length ?? 0

  if (Number.isFinite(total) && total > recibidas) {
    reportarAdvertencia('El Kanban recibió menos reservas de las que reporta el backend', {
      total,
      recibidas,
    })
  }

  return response
}

export const reservaCentralizadaApi = createApi({
  reducerPath: 'reservaCentralizadaApi',
  baseQuery,
  endpoints: (builder) => ({
    getBookings: builder.query<
      GenericResponseOData<ReservaKanban[]>,
      GetBookingParams
    >({
      query: (args: GetBookingParams) => ({
        url: `/ReservasCentralizadas?${getQueryReservasOData(args)}`,
        method: 'GET',
      }),
      transformResponse: avisarSiFaltanFilas,
      keepUnusedDataFor: 0,
    }),
    getBookingPorId: builder.query<
      GenericResponseOData<ReservaKanban[]>,
      { reservaCentralizadaId: number; menuId: number }
    >({
      query: ({ reservaCentralizadaId, menuId }) => ({
        url: `/ReservasCentralizadas?${getQueryReservaPorIdOData(reservaCentralizadaId, menuId)}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),
    updateReservaCentralizada: builder.mutation<void, ReservaCentralizadaBase>({
      query: (body) => ({
        url: `/ReservasCentralizadas(${body.ReservaCentralizadaId})`,
        method: 'PUT',
        body,
      }),
    }),
    getBookingWithFullSearch: builder.mutation<
      GenericResponseOData<ReservaKanban[]>,
      GetBookingWithFullSearchParams
    >({
      query: (body) => ({
        url: `/ReservaCentralizadaBusiness/ConsultarReservas`,
        method: 'POST',
        body,
      }),
    }),
  }),
})

export const {
  useLazyGetBookingsQuery,
  useLazyGetBookingPorIdQuery,
  useUpdateReservaCentralizadaMutation,
  useGetBookingWithFullSearchMutation,
} = reservaCentralizadaApi
